import { useLoaderData, useNavigate, useSearchParams, Form } from 'react-router';
import { requireUser } from '../../../lib/session.server';
import { jobsApi, strategyApi } from '../../../lib/api';
import type { LoaderFunctionArgs } from 'react-router';
import { useState, useEffect } from 'react';
import { Search, MapPin, X, Filter } from 'lucide-react';
import { Card, Button } from '../../../components/ui';
import { JobCard, JobDetailSheet } from '../../../components/jobs';
import { useToastActions } from '../../../components/ui';

import type { Job, SearchResponse, JobSearchFilters, User } from '../../../types';

interface SearchData {
  user: User;
  searchResponse: SearchResponse;
  query: string;
  filters: JobSearchFilters;
  page: number;
}

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  const url = new URL(request.url);
  
  const query = url.searchParams.get('q') || '';
  const page = parseInt(url.searchParams.get('page') || '1', 10);
  
  const filters: JobSearchFilters = {
    location: url.searchParams.get('location') || undefined,
    seniority: url.searchParams.get('seniority') || undefined,
    remote: url.searchParams.get('remote') === 'true' ? true : undefined,
    salary_min: url.searchParams.get('salary_min') ? parseInt(url.searchParams.get('salary_min')!) : undefined,
    salary_max: url.searchParams.get('salary_max') ? parseInt(url.searchParams.get('salary_max')!) : undefined,
    employment_type: url.searchParams.getAll('employment_type').filter(Boolean),
    company_size: url.searchParams.getAll('company_size').filter(Boolean),
    posted_days: url.searchParams.get('posted_days') ? parseInt(url.searchParams.get('posted_days')!) : undefined,
  };

  if (!query && !filters.location) {
    const emptyResponse: SearchResponse = {
      jobs: [],
      total_count: 0,
      page: 1,
      page_size: 20,
      has_more: false,
    };
    
    return {
      user,
      searchResponse: emptyResponse,
      query: '',
      filters,
      page: 1,
    };
  }

  try {
    const searchResponse = await jobsApi.search({
      q: query || undefined,
      filters,
      page,
      pageSize: 20,
      sort: 'relevance',
      engine: 'auto',
      include_attribution: true,
    });

    return {
      user,
      searchResponse,
      query,
      filters,
      page,
    };
  } catch (error) {
    const emptyResponse: SearchResponse = {
      jobs: [],
      total_count: 0,
      page,
      page_size: 20,
      has_more: false,
    };
    
    return {
      user,
      searchResponse: emptyResponse,
      query,
      filters,
      page,
      error: 'Failed to search jobs',
    };
  }
}

export function JobSearchPage() {
  const { searchResponse, query, filters, page } = useLoaderData() as SearchData;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [jobs, setJobs] = useState(searchResponse.jobs);
  const [searchQuery, setSearchQuery] = useState(query);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [showJobDetail, setShowJobDetail] = useState(false);
  const { success, error } = useToastActions();

  useEffect(() => {
    setJobs(searchResponse.jobs);
  }, [searchResponse.jobs]);

  const handleSave = async (jobId: string) => {
    try {
      const job = jobs.find(j => j.id === jobId);
      if (job?.saved) {
        await jobsApi.unsaveJob(jobId);
        success('Job Unsaved', 'Job removed from saved jobs.');
      } else {
        await jobsApi.saveJob(jobId, { bucket: 'to_apply' });
        success('Job Saved', 'Job added to your saved jobs.');
      }
      
      // Record interaction for strategy learning
      await strategyApi.recordReward({
        user_id: 'current',
        job_id: jobId,
        action: job?.saved ? 'reject' : 'save',
        strategy_key: job?.attribution?.strategy_key,
        query_id: job?.attribution?.query_id,
      });
      
      setJobs(jobs.map(j => 
        j.id === jobId ? { ...j, saved: !j.saved } : j
      ));
    } catch (err) {
      error('Action Failed', 'Unable to save job. Please try again.');
    }
  };

  const handleReject = async (jobId: string) => {
    try {
      await jobsApi.rejectJob(jobId);
      
      // Record rejection for strategy learning
      const job = jobs.find(j => j.id === jobId);
      await strategyApi.recordReward({
        user_id: 'current',
        job_id: jobId,
        action: 'reject',
        strategy_key: job?.attribution?.strategy_key,
        query_id: job?.attribution?.query_id,
      });
      
      setJobs(jobs.filter(j => j.id !== jobId));
      success('Job Rejected', 'This job will not appear in future recommendations.');
    } catch (err) {
      error('Action Failed', 'Unable to reject job. Please try again.');
    }
  };

  const handleApply = async (jobId: string) => {
    const job = jobs.find(j => j.id === jobId);
    if (job?.url) {
      // Record application intent for strategy learning
      await strategyApi.recordReward({
        user_id: 'current',
        job_id: jobId,
        action: 'apply',
        strategy_key: job.attribution?.strategy_key,
        query_id: job.attribution?.query_id,
      });
      
      window.open(job.url, '_blank');
    } else {
      navigate(`/jobs/${jobId}/apply`);
    }
  };

  const handleJobClick = async (jobId: string) => {
    const job = jobs.find(j => j.id === jobId);
    if (job) {
      setSelectedJob(job);
      setShowJobDetail(true);
      
      // Record view for analytics
      await jobsApi.recordView(jobId, {
        source: 'search',
        strategy_key: job.attribution?.strategy_key,
        query_id: job.attribution?.query_id,
      });
    }
  };

  const formatTags = (job: Job): string[] => {
    const tags: string[] = [];
    if (job.tags) {
      tags.push(...job.tags);
    }
    if (job.salaryMin || job.salaryMax) {
      const salaryRange = job.salaryMin && job.salaryMax 
        ? `$${(job.salaryMin / 1000).toFixed(0)}k-$${(job.salaryMax / 1000).toFixed(0)}k`
        : job.salaryMin 
        ? `$${(job.salaryMin / 1000).toFixed(0)}k+`
        : `Up to $${(job.salaryMax! / 1000).toFixed(0)}k`;
      tags.push(salaryRange);
    }
    return tags;
  };

  return (
    <div className="space-y-6">
      {/* Header and Search */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Search className="h-6 w-6 text-primary" />
          <h1 className="text-3xl font-bold text-foreground">Job Search</h1>
        </div>
        
        <Card>
          <Form method="get" className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <input
                type="text"
                name="q"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for jobs by title, company, or keywords..."
                className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
            </div>
            <Button type="submit" className="gap-2">
              <Search className="h-4 w-4" />
              Search
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setShowFilters(!showFilters)}
              className="lg:hidden gap-2"
            >
              <Filter className="h-4 w-4" />
              Filters
            </Button>
          </Form>
          
          {/* Attribution Summary */}
          {searchResponse.attribution_summary && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <span>
                  Strategies: <strong className="text-foreground">
                    {searchResponse.attribution_summary.strategies_used.length}
                  </strong>
                </span>
                <span>
                  Exploration: <strong className="text-foreground">
                    {(searchResponse.attribution_summary.exploration_rate * 100).toFixed(0)}%
                  </strong>
                </span>
                <span>
                  Queries: <strong className="text-foreground">
                    {searchResponse.attribution_summary.total_queries}
                  </strong>
                </span>
              </div>
            </div>
          )}
        </Card>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Filters Sidebar */}
        <aside className={`lg:w-1/4 ${showFilters ? 'block' : 'hidden lg:block'}`}>
          <Card>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-foreground">Filters</h3>
              
              <Form method="get" className="space-y-4">
                <input type="hidden" name="q" value={query} />
                
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Location
                    {filters.location && (
                      <button
                        type="button"
                        onClick={() => {
                          const newParams = new URLSearchParams(searchParams);
                          newParams.delete('location');
                          setSearchParams(newParams);
                        }}
                        className="ml-auto text-muted-foreground hover:text-foreground"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </label>
                  <input
                    type="text"
                    name="location"
                    defaultValue={filters.location}
                    placeholder="e.g., San Francisco, CA"
                    className="w-full px-3 py-2 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Seniority Level</label>
                  <select 
                    name="seniority" 
                    defaultValue={filters.seniority} 
                    className="w-full px-3 py-2 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="">All Levels</option>
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                    <option value="lead">Lead/Manager</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="remote"
                    name="remote"
                    defaultChecked={filters.remote}
                    className="rounded border-border text-primary focus:ring-primary/50"
                  />
                  <label htmlFor="remote" className="text-sm text-foreground">Remote Only</label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Min Salary</label>
                  <input
                    type="number"
                    name="salary_min"
                    defaultValue={filters.salary_min}
                    placeholder="e.g., 50000"
                    className="w-full px-3 py-2 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Max Salary</label>
                  <input
                    type="number"
                    name="salary_max"
                    defaultValue={filters.salary_max}
                    placeholder="e.g., 150000"
                    className="w-full px-3 py-2 bg-background border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">Posted Within</label>
                  <select 
                    name="posted_days" 
                    defaultValue={filters.posted_days} 
                    className="w-full px-3 py-2 bg-background border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="">Any time</option>
                    <option value="1">Last 24 hours</option>
                    <option value="3">Last 3 days</option>
                    <option value="7">Last week</option>
                    <option value="30">Last month</option>
                  </select>
                </div>

                <Button type="submit" className="w-full">
                  Apply Filters
                </Button>
              </Form>
            </div>
          </Card>
        </aside>

        {/* Job Results */}
        <main className="lg:w-3/4">
          {jobs.length === 0 ? (
            <Card>
              <div className="text-center py-12">
                <Search className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-foreground mb-2">No jobs found</h3>
                <p className="text-muted-foreground">
                  {query ? "Try adjusting your search criteria or filters" : "Start by searching for jobs"}
                </p>
              </div>
            </Card>
          ) : (
            <>
              <div className="mb-6 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Found <strong className="text-foreground">{searchResponse.total_count}</strong> jobs matching your criteria
                </div>
                {searchResponse.has_more && (
                  <div className="text-xs text-muted-foreground">
                    Page {page} of {Math.ceil(searchResponse.total_count / searchResponse.page_size)}
                  </div>
                )}
              </div>
              
              <div className="space-y-4">
                {jobs.map((job) => (
                  <div key={job.id}>
                    <JobCard
                      job={job}
                      onSave={handleSave}
                      onReject={handleReject}
                      onApply={handleApply}
                      onClick={handleJobClick}
                    />
                    {formatTags(job).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {formatTags(job).slice(0, 3).map((tag, idx) => (
                          <span key={idx} className="text-xs bg-muted px-2 py-1 rounded-lg text-muted-foreground">
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Pagination */}
              {searchResponse.has_more && (
                <div className="flex justify-center gap-2 mt-6">
                  {page > 1 && (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        searchParams.set('page', (page - 1).toString());
                        setSearchParams(searchParams);
                      }}
                    >
                      Previous
                    </Button>
                  )}
                  
                  <div className="flex items-center px-4 py-2 text-sm text-muted-foreground">
                    Page {page} of {Math.ceil(searchResponse.total_count / searchResponse.page_size)}
                  </div>
                  
                  {page < Math.ceil(searchResponse.total_count / searchResponse.page_size) && (
                    <Button
                      variant="secondary"
                      onClick={() => {
                        searchParams.set('page', (page + 1).toString());
                        setSearchParams(searchParams);
                      }}
                    >
                      Next
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </main>
      </div>

      {/* Job Detail Sheet */}
      <JobDetailSheet
        job={selectedJob}
        isOpen={showJobDetail}
        onClose={() => {
          setShowJobDetail(false);
          setSelectedJob(null);
        }}
        onSave={handleSave}
        onReject={handleReject}
        onApply={handleApply}
      />
    </div>
  );
}